<template>
  <div
    v-if="shouldShowAd(topScroller) && !isPreview"
    class="top-ad-placeholder"
  >
    <dn-ad
      :ad="topScroller"
      class="full top-ad-above-header"
    />
  </div>
  <client-only v-if="!(routeName === 'article' && isPreview) && shouldShowAd(magStripe)">
    <dn-ad
      :ad="magStripe"
      class="mag-stripe"
    />
  </client-only>
  <template v-if="!(routeName === 'article' && isPreview) && shouldShowAd(topBanner) && topBanner?.aboveHeader && !renderTopBannerBeforeBodyText(topBanner)">
    <div class="top-ad-placeholder">
      <dn-ad
        :key="topBanner.id"
        :ad="topBanner"
        class="full top-ad-above-header"
        data-zone="top-ad"
      />
    </div>
  </template>
  <dn-header-wrapper v-if="!isAppView" />
  <dn-ticker v-if="routeName === 'frontpage' && investorTickerEnabled" />
  <div :class="['dn-page-layout', { 'app-view': isAppView }]">
    <client-only v-if="routeName === 'article' && !isPreview && (shouldShowAd(leftMediumRectangle) || shouldShowAd(leftSkyscraper))">
      <div
        class="left-sticky-container"
        :style="{height: stickyHeight}"
      >
        <dn-ad
          v-if="shouldShowAd(leftMediumRectangle)"
          :ad="leftMediumRectangle"
          :class="{ [leftMediumRectangle.class]: true }"
        />
        <dn-ad
          v-if="shouldShowAd(leftSkyscraper)"
          :ad="leftSkyscraper"
          :class="{ [leftSkyscraper.class]: true , 'skyscraper-preview': isPreview}"
        />
      </div>
    </client-only>
    <client-only v-if="routeName === 'article' && !isPreview && (shouldShowAd(rightMediumRectangle) || shouldShowAd(rightSkyscraper))">
      <div
        class="right-sticky-container"
        :style="{height: stickyHeight}"
      >
        <dn-ad
          v-if="shouldShowAd(rightMediumRectangle)"
          :ad="rightMediumRectangle"
          :class="{ [rightMediumRectangle.class]: true, 'medium-rectangle-preview': isPreview }"
        />
        <dn-ad
          v-if="shouldShowAd(rightSkyscraper)"
          :ad="rightSkyscraper"
          :class="{ [rightSkyscraper.class]: true,'skyscraper-preview': isPreview }"
        />
      </div>
    </client-only>
    <div class="content">
      <slot />
    </div>
  </div>
  <dn-ma-frame
    v-if="!isPreview && shouldRenderBannerCard(stickyBottomCard)"
    :id="stickyBottomCard.id"
    class="dn-ma-frame-bottom-sticky"
  />
  <atlas-footer
    v-if="!isAppView"
  />
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { AtlasFooter } from '@nhst/ui';
import { useCommonStore } from '~/store/common';
import { useArticleStore } from '~/store/article';
import type { ElementPositionPayload } from '~/types/elementPositionPayload';

const commonStore = useCommonStore();
const isAppView = commonStore.isAppView;
const route = useRoute();
const articleStore = useArticleStore();

const rtConfPub = useRuntimeConfig().public;
const stickyHeight = ref('');

const routeName = computed(() => {
  const name = getRouteName(route.name);
  if (name === 'latest' || name === 'search') {
    return 'section';
  } else {
    return name;
  }
});

// Make isPreview reactive
const isPreview = ref(isPreviewMode());
// Provide to child components
provide('isPreview', isPreview);

// When on the article page, suppress top ads if article.metadata.removeTopAds is true
const removeTopAds = computed(() =>
  routeName.value === 'article' &&
  articleStore.currentArticle?.metadata?.removeTopAds === true
);

// onUpdate it will be reset to current route
let lastRouteName = routeName.value;

let unsubscribeListener: (() => void) | null = null;

const adConfig = getAdConfig();

const topScroller = computed(() => adConfig?.[routeName.value]?.static?.topScroller);
const magStripe = computed(() => adConfig?.[routeName.value]?.static?.magStripe);
const topBanner = computed(() => !removeTopAds.value ? adConfig?.[routeName.value]?.static?.topBanner : null);
const leftMediumRectangle = computed(() => adConfig?.[routeName.value]?.static?.leftMediumRectangle);
const leftSkyscraper = computed(() => adConfig?.[routeName.value]?.static?.leftSkyscraper);
const rightMediumRectangle = computed(() => adConfig?.[routeName.value]?.static?.rightMediumRectangle);
const rightSkyscraper = computed(() => adConfig?.[routeName.value]?.static?.rightSkyscraper);

const stickyBottomCard = computed(() => getBrazeConfig().bannerConfig?.[routeName.value]?.static?.stickyBottom);
const investorTickerEnabled = rtConfPub?.investorTicker ?? false;

onMounted(() => {
  unsubscribeListener = useBusListener({
    'sticky-height-event': (payload: ElementPositionPayload) => {
      stickyHeight.value = payload?.stickyHeight > 0 ? payload?.stickyHeight + 'px' : '';
    }
  });
});

onUnmounted(() => {
  if (unsubscribeListener) {
    unsubscribeListener?.();
  }
});

watch(() => route.name, (_, lastName) => {
  lastRouteName = getRouteName(lastName);
  resetOrRefreshAds(adConfig, routeName.value, lastRouteName);
}, { deep: true });

onUpdated(() => {
  if (routeName.value !== lastRouteName) {
    const reloadableAds = [];
    if (shouldShowAd(topScroller.value)) {
      reloadableAds.push(topScroller.value.id);
    }
    if (shouldShowAd(magStripe.value)) {
      reloadableAds.push(magStripe.value.id);
    }
    reloadableAds.forEach((id) => {
      loadAd(id);
    });
    lastRouteName = routeName.value;
  }
});

</script>

<style lang="scss">
body {
  margin: 0;
  padding: 0;
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  background-color: var(--color-page-bg);
  color: var(--color-typography-default);
  font-family: var(--typography-editorial-body-max-font-family);
}

* {
  box-sizing: border-box;
}

.dn-page-layout {
  display: flex;
  flex-direction: column;
  position: relative;

  @media only screen and (width >= 1280px) {
    display: grid;
    grid-template-areas:
      'top top top'
      'ad-left header ad-right'
      'ad-left content ad-right'
      'ad-left feed ad-right';
    grid-template-columns: 3fr var(--article-width) 3fr;
    column-gap: var(--ui-gap);
    align-items: start;
    flex-wrap: wrap;
  }

  > .content {
    grid-area: content;
    grid-column: 1 / 4;
  }
}

.top-ad-placeholder {
  .top-ad-above-header{
    padding: var(--row-gap) 0;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
